import { type FieldValues, useController } from 'react-hook-form';

import type { ComponentProps } from '~/chords/ComponentProps';
import {
  DropdownSingleSelect,
  type DropdownSingleSelectProps,
} from '~/chords/DropdownSingleSelect/DropdownSingleSelect';
import type {
  ExternalDropdownOption,
  ExternalDropdownOptionsFlat,
} from '~/chords/DropdownSingleSelect/types';
import { FormField, type FormFieldProps } from '~/chords/FormField';
import { MultiSelect } from '~/chords/MultiSelect/MultiSelect';

type FormFieldDropdownMultiSelectProps<
  TOptionProps,
  TFormFields extends FieldValues,
> = Omit<FormFieldProps<TFormFields>, 'children'> &
  Omit<
    DropdownSingleSelectProps<TOptionProps>,
    | 'name'
    | 'className'
    | 'value'
    | 'onValueChange'
    | 'id'
    | 'disabled'
    | 'state'
  > & {
    options: ExternalDropdownOptionsFlat<TOptionProps>;
    onValueChange?: (value: ExternalDropdownOption<TOptionProps>[]) => void;
  };

export function FormFieldDropdownMultiSelect<
  TOptionProps = unknown,
  TFormFields extends FieldValues = FieldValues,
>({
  name,
  id,
  label,
  dangerouslyOverrideClassName,
  disabled = false,
  readOnly = false,
  labelAddon,
  fieldMessageClassName,
  showErrorMessage = true,
  onValueChange,
  options,
  ...dropdownProps
}: ComponentProps<
  FormFieldDropdownMultiSelectProps<TOptionProps, TFormFields>
>) {
  const {
    field: { ref: _ref, value, onChange, onBlur },
    fieldState: { invalid },
  } = useController<TFormFields>({ name, disabled });

  const selectedItems = (value ?? []) as ExternalDropdownOption<TOptionProps>[];

  return (
    <FormField
      name={name}
      id={id}
      label={label}
      dangerouslyOverrideClassName={dangerouslyOverrideClassName}
      disabled={disabled}
      readOnly={readOnly}
      labelAddon={labelAddon}
      fieldMessageClassName={fieldMessageClassName}
      showErrorMessage={showErrorMessage}
    >
      <MultiSelect<ExternalDropdownOption<TOptionProps>>
        value={selectedItems}
        onValueChange={(items) => {
          onChange(items);
          onBlur();
          onValueChange?.(items);
        }}
        getItemLabel={(option) => option.label ?? ''}
        getItemValue={(option) => option.value}
        disabled={disabled || readOnly}
        renderDropdown={({
          onSelect,
          selectedValues,
          disabled: isDisabled,
        }) => (
          <DropdownSingleSelect
            {...dropdownProps}
            id={id || name}
            options={options.filter((opt) => !selectedValues.has(opt.value))}
            value=""
            onValueChange={(val) => {
              const option = options.find((opt) => opt.value === val);

              if (option) {
                onSelect(option);
              }
            }}
            onOpenChange={(isOpen) => {
              if (!isOpen) {
                onBlur();
              }

              dropdownProps.onOpenChange?.(isOpen);
            }}
            disabled={isDisabled}
            state={invalid ? 'error' : 'default'}
          />
        )}
      />
    </FormField>
  );
}

FormFieldDropdownMultiSelect.displayName = 'FormFieldDropdownMultiSelect';
